import * as authAPI from '../../../api/auth';
import * as processAPI from '../../../api/process';
import * as clientsAPI from '../../../api/clients';
import * as fieldValuesAPI from '../../../api/fieldValue';
import * as sharedFieldValuesAPI from '../../../api/sharedFieldValues';

import { AuthError } from '../../../utils';
import { getLeadId } from '../../../utils/leads';
import { getSourceLabel } from '../../../utils/flows';
import { generateSharedFieldValues } from '../../../utils/clients';
import { getRegisterClientFields, storeClientData } from '../../../utils/clientData';
import { generateTMPPasswordField, generateUserDataFromFieldsData } from '../utils/users';

import { LEAD_OWNER_MODEL } from '../constants/leads';
import { createLeadAndNotify } from '../../../utils/api/leads.utils';
import {
  AGENTE_INMOBILIARIO_FIELD_ID,
  COMPANY,
  INICIA_TRAMITE_TIMELINE_TAG,
  LOGIN_PHONE_ID,
  NOMBRE_INMOBILIARIA_FIELD_ID,
} from '../../../constants';
import { completeTimelineTagByTagName } from '../../../api/timeline';
import { getConfigs } from '../../../utils/configs';

export const createProcess = async ({
  flow, client, token, productId, company,
}) => {
  const dataToUpload = {
    flow,
    client,
    fields: [],
    product: productId,
  };
  return processAPI.createProcess(dataToUpload, company, token);
};

export const saveSharedFieldValues = async (fields, clientId, token, ownerModel) => {
  const dataSharedFieldValues = generateSharedFieldValues(fields, clientId, ownerModel);
  await Promise.all(
    dataSharedFieldValues.map(
      async (itemSharedFieldValue) => sharedFieldValuesAPI
        .saveSharedFieldValuesData(
          itemSharedFieldValue,
          token,
        ),
    ),
  );
};

export const createClient = async (dataToUpload) => {
  const clientReponse = await clientsAPI.saveFieldsData(dataToUpload);
  const { client, exists = false, token = null } = clientReponse;
  return { client, exists, token };
};

export const createClientAccount = () => {

};

export const loginByEmailAndPassword = async (email, password) => {
  try {
    const payloadConfig = await getConfigs(LOGIN_PHONE_ID);
    const { clientRoles = [] } = payloadConfig;
    const { user } = await authAPI.loginByCredentials(email, password, clientRoles);
    return user;
  } catch (e) {
    throw new AuthError();
  }
};

export const storeClientFieldsData = (registerFields, sharedFieldValues) => {
  const item = {
    registerFields,
    sharedFieldValues,
  };
  storeClientData(item);
};

/**
 * Genera una contraseña aleatoria criptográficamente segura y única por cliente.
 * Sustituye a la contraseña temporal fija compartida entre todos los clientes,
 * que permitía que quien conociera ese valor accediera a cualquier cuenta creada
 * por este flujo. Al crear e iniciar sesión de forma inmediata, el valor no
 * necesita persistirse en ningún otro lugar.
 */
const generateSecureRandomPassword = (length = 32) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const cryptoObj = window.crypto || window.msCrypto;
  const randomValues = new Uint32Array(length);
  cryptoObj.getRandomValues(randomValues);
  return Array.from(randomValues, (value) => charset[value % charset.length]).join('');
};

export const createClientAccountFromLocalData = async () => {
  const clietData = getRegisterClientFields();
  const passwordField = { ...generateTMPPasswordField(), value: generateSecureRandomPassword() };
  const { value: password } = passwordField;
  const newClientData = {
    ...clietData,
    fields: [...clietData.fields, passwordField],
  };
  const { client, exists, token } = await createClient(newClientData);
  return {
    client, password, exists, token,
  };
};

/**
 * Obtiene el usuario autenticado (con su token) tras la creación del cliente.
 * - Si el cliente es nuevo (o el backend no devolvió token), inicia sesión con la
 *   contraseña recién generada.
 * - Si el cliente ya existía, el backend devuelve un token de sesión: se
 *   intercambia por el usuario completo (con `_id`, `roles`, etc.) vía
 *   `loginByToken`, evitando el login por email/contraseña, que fallaría porque
 *   la contraseña pudo haber sido asignada por otra app y no coincide con la de
 *   este flujo.
 * Devuelve siempre un objeto de usuario consistente que incluye el `token`.
 */
export const resolveClientSession = async ({
  client, password, exists, token,
}) => {
  const { email } = client || {};
  if (!exists || !token) {
    return loginByEmailAndPassword(email, password);
  }
  try {
    const { user } = await authAPI.loginByToken(email, token);
    return { ...user, token: user?.token || token };
  } catch (e) {
    throw new AuthError();
  }
};

const addInmobiliaria = (clientData, inmobiliaria) => {
  if (!inmobiliaria) return clientData;
  const newCliente = {
    ...clientData,
    extraData: {
      inmobiliaria,
    },
  };
  return newCliente;
};

export const createLeadFromClientData = async (clientFields, company, inmobiliaria) => {
  const source = getSourceLabel();
  const clientObject = generateUserDataFromFieldsData(clientFields);
  const newClientObject = addInmobiliaria(clientObject, inmobiliaria);
  const createdLead = await createLeadAndNotify({ ...newClientObject, source, company });
  return createdLead;
};

export const saveLeadSharedFieldValues = async (fields, leadId) => {
  await saveSharedFieldValues(fields, leadId, '', LEAD_OWNER_MODEL);
};

export const checkAndSaveLeadSharedData = async (fields) => {
  const leadId = getLeadId();
  if (!leadId) return;
  await saveLeadSharedFieldValues(fields, leadId);
};

export const updateClientByAgentAssigned = async ({ client, agentId, token }) => {
  const { _id } = client;
  const newClient = { ...client, agentAssigned: agentId };
  await clientsAPI.updateClientData(_id, newClient, token);
};

export const updateProcessByAgentAssigned = async ({ process, agentId, token }) => {
  const { _id } = process;
  const newProcess = { ...process, agentAssigned: agentId };
  await processAPI.updateProcess(newProcess, _id, token);
};

export const updateClientDataByAgentAssigned = async ({
  client, agentId, process, token,
}) => {
  if (!agentId) return;
  await updateProcessByAgentAssigned({ process, agentId, token });
  await updateClientByAgentAssigned({ client, agentId, token });
};

export const completeClientIniciaTramiteTag = async (processId, token) => {
  await completeTimelineTagByTagName(
    processId,
    COMPANY,
    { tagName: INICIA_TRAMITE_TIMELINE_TAG },
    token,
  );
};

export const saveFieldValueInmobiliaria = async (value, process, token) => {
  fieldValuesAPI.saveFieldValue({
    field: '6567e60c1f07e5a5c2288d88',
    value,
    process,
  }, token);
};

export const saveFieldValueAgentSelected = async (value, process, token) => {
  fieldValuesAPI.saveFieldValue({
    field: AGENTE_INMOBILIARIO_FIELD_ID,
    value,
    process,
  }, token);
};

/** Guarda un valor en un campo por su ID (usado para agente/asesor según fuente de interés) */
export const saveFieldValueForField = async (fieldId, value, processId, token) => {
  await fieldValuesAPI.saveFieldValue({
    field: fieldId,
    value,
    process: processId,
  }, token);
};

export const saveFieldValueNombreInmobiliaria = async (value, process, token) => {
  fieldValuesAPI.saveFieldValue({
    field: NOMBRE_INMOBILIARIA_FIELD_ID,
    value,
    process,
  }, token);
};

export const updateProcessData = async ({ process, token }) => {
  await processAPI.updateProcess(process, process._id, token);
};
